import { useEffect, useState, useCallback } from "react";

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "jp-voice-theme";

const getSystemTheme = (): ResolvedTheme =>
  window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

const resolveTheme = (theme: Theme): ResolvedTheme =>
  theme === "system" ? getSystemTheme() : theme;

const applyTheme = (resolved: ResolvedTheme) => {
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
};

export const getStoredTheme = (): Theme => {
  if (typeof window === "undefined") return "dark";
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === "light" || v === "dark" || v === "system") return v;
  return "dark";
};

export const setTheme = (theme: Theme) => {
  window.localStorage.setItem(STORAGE_KEY, theme);
  applyTheme(resolveTheme(theme));
  window.dispatchEvent(new CustomEvent("jp-voice-theme-change", { detail: theme }));
};

export const initTheme = () => {
  if (typeof window === "undefined") return;
  applyTheme(resolveTheme(getStoredTheme()));
};

export const useTheme = () => {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(getStoredTheme()));

  useEffect(() => {
    const onChange = (e: Event) => {
      const next = (e as CustomEvent<Theme>).detail;
      setThemeState(next);
      setResolved(resolveTheme(next));
    };
    window.addEventListener("jp-voice-theme-change", onChange);

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onSystemChange = () => {
      if (getStoredTheme() === "system") {
        setResolved(getSystemTheme());
        applyTheme(getSystemTheme());
      }
    };
    mql.addEventListener("change", onSystemChange);

    return () => {
      window.removeEventListener("jp-voice-theme-change", onChange);
      mql.removeEventListener("change", onSystemChange);
    };
  }, []);

  const update = useCallback((t: Theme) => setTheme(t), []);
  const toggle = useCallback(() => {
    setTheme(resolved === "dark" ? "light" : "dark");
  }, [resolved]);

  return { theme, resolved, setTheme: update, toggle };
};
